import {
  Column,
  ConfirmModal,
  HintLabel,
  SaveBar,
  TableFrame,
  TableHeader,
} from '@skupreme/shared/ui';
import { useAuth } from '@contexts/auth';
import { useSidebar } from '@contexts/sidebar';
import useUrlPermission from '@hooks/url-permission';
import { CommonUtility } from '@utility/common';
import { reservedQTYTooltip, tooltipTexts } from '@utility/constants/common';
import StorageProviderService from '@utility/services/storage-provider';
import { ToastMessage } from '@utility/toast';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ISortObject } from '../../legacy-bridge';
import { IntegrationType } from '../../lib/integration';
import {
  buildAliasRemoval,
  buildInventoryUpdate,
  type CartonQuantities,
  cartonsColumnHeader,
  changedItems,
  type InventoryColumn,
  type InventoryFlags,
  type InventoryItem,
  initialCartons,
  initialLoose,
  inventoryColumns,
  type LooseQuantities,
  mergeLotsByAlias,
  showsLooseColumn,
} from '../../lib/inventory';
import { useProviderInventory } from '../../hooks/use-provider-tabs';
import { fromSortDescriptor, toSortDescriptor } from '../../lib/list-sort';
import { CartonAliasModal, type CartonAliasTarget } from './CartonAliasModal';
import {
  type AliasRemoval,
  type InventoryRowContext,
  ItemRows,
} from './InventoryRows';
import { InventoryToolbar } from './InventoryToolbar';
import { ItemSettingsModal } from './ItemSettingsModal';
import { ProviderKindCard } from './ProviderKindCard';

export type InventoryProvider = {
  _id?: string;
  integration_type?: string;
  is_fulfillable?: boolean;
  is_warehouse?: boolean;
  display_setting?: string;
  marketplace_region_id?: { name?: string };
};

/* Providers whose stock can be pulled again on demand. */
const REFRESHABLE: string[] = [
  IntegrationType['3pl'],
  IntegrationType.apiFtp,
  IntegrationType.deliverr,
  IntegrationType.farfill,
  IntegrationType.goodCang,
  IntegrationType.logistics_plus,
  IntegrationType.mintsoft,
  IntegrationType.santaFe,
  IntegrationType.shipHero,
  IntegrationType.skudrop,
  IntegrationType.tactical3pl,
  IntegrationType.camelot,
  IntegrationType.lyb,
];

/* Units are read most-first; titles start A→Z. */
const FIRST_DIRECTION: Record<string, 'asc' | 'desc'> = {
  available: 'desc',
};

const COLUMN_ID: Partial<Record<InventoryColumn, string>> = {
  product: 'title',
  cartons: 'available',
};

const SORTABLE = new Set<InventoryColumn>(['product', 'inbound', 'cartons']);

function ReservedHint({ flags }: { flags: InventoryFlags }) {
  const key = flags.isFBA ? 'fba' : flags.isDeliverr ? 'deliverr' : 'storage';

  return (
    <span className="sk:flex sk:max-w-80 sk:flex-col sk:gap-1 sk:whitespace-normal">
      <strong>{reservedQTYTooltip[`${key}Header`]}</strong>
      <span>{reservedQTYTooltip[`${key}Content`]}</span>
    </span>
  );
}

type SidebarData = {
  general_sheets?: { type: string; sheet_id?: string }[];
};

/** A provider's stock per item: cartons, loose units, aliases and inbound. */
export function InventoryTab({
  provider,
  providerId,
  loading: detailLoading,
  imperial,
  setImperial,
  initialSearch,
  readOnly,
  isUser,
  refreshDetails,
}: {
  provider?: InventoryProvider;
  providerId: string;
  loading: boolean;
  imperial: boolean;
  setImperial: (imperial: boolean) => void;
  initialSearch?: string | null;
  readOnly: boolean;
  isUser: boolean;
  refreshDetails: () => void;
}) {
  const permission = useUrlPermission();
  const { isStarterPlan } = useAuth();
  const { data: sidebar } = useSidebar() as { data?: SidebarData };

  const integration = provider?.integration_type;
  const isWarehouse = !!provider?.is_warehouse;
  const isFBA = integration === 'spapi';
  const isDeliverr = integration === 'deliverr';
  const isAWD = integration === 'awd';
  const flags: InventoryFlags = useMemo(
    () => ({
      isFBA,
      isDeliverr,
      isManual: integration === 'manual',
      isWarehouse,
      /* FBA Merchant fulfills orders even when the provider never said so. */
      isFulfillable:
        !!provider?.is_fulfillable || (integration === 'fba_m' && !isWarehouse),
    }),
    [isFBA, isDeliverr, integration, isWarehouse, provider?.is_fulfillable],
  );

  const {
    data: apiData,
    loading,
    refresh,
    filter,
    filterChanged,
    pageChanged,
    totalData,
    showZeroInventory,
    setShowZeroInventory,
    search,
    setSearch,
  } = useProviderInventory(providerId);

  const items = useMemo(
    () =>
      readOnly
        ? mergeLotsByAlias(apiData as InventoryItem[])
        : (apiData as InventoryItem[]),
    [apiData, readOnly],
  );

  const [initial, setInitial] = useState<CartonQuantities>({});
  const [cartons, setCartons] = useState<CartonQuantities>({});
  const [loose, setLoose] = useState<LooseQuantities>({});
  const [hasLoose, setHasLoose] = useState(false);
  const [processing, setProcessing] = useState<Set<string>>(new Set());
  const [onceSubmitted, setOnceSubmitted] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [kindSaved, setKindSaved] = useState(false);
  const [aliasTarget, setAliasTarget] = useState<CartonAliasTarget | null>(
    null,
  );
  const [settingsItem, setSettingsItem] = useState<InventoryItem | null>(null);
  const [removal, setRemoval] = useState<AliasRemoval | null>(null);

  const reset = useCallback(() => {
    const start = initialCartons(items, readOnly);
    const unaliased = initialLoose(items, readOnly);
    setInitial(start);
    setCartons(start);
    setLoose(unaliased.loose);
    setHasLoose(isWarehouse && unaliased.hasLoose);
  }, [items, readOnly, isWarehouse]);

  useEffect(reset, [reset]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only a new search from the URL
  useEffect(() => {
    if (initialSearch) setSearch(initialSearch);
  }, [initialSearch]);

  /* A US provider opens in inches and pounds. */
  const regionCode = provider?.marketplace_region_id?.name;
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per region
  useEffect(() => {
    if (regionCode?.includes('US')) setImperial(true);
  }, [regionCode]);

  const changed = useMemo(
    () => changedItems(cartons, initial, loose),
    [cartons, initial, loose],
  );

  const showLooseColumn = showsLooseColumn(flags, hasLoose);
  const columns = inventoryColumns({ ...flags, showLooseColumn });
  const cartonsHeader = cartonsColumnHeader(flags, showLooseColumn);
  const isKindMissing =
    !detailLoading &&
    CommonUtility.isValidObject(provider) &&
    !flags.isFulfillable &&
    !flags.isWarehouse &&
    !kindSaved;

  const sheetId = sidebar?.general_sheets?.find(
    sheet => sheet.type === 'inventory',
  )?.sheet_id;

  const save = async () => {
    try {
      setSaving(true);
      await StorageProviderService.updateItems(
        providerId,
        buildInventoryUpdate(items, cartons, loose, flags),
      );
      ToastMessage.success('Items updated successfully.');
      refresh();
      setOnceSubmitted(true);
      setProcessing(current => new Set([...current, ...changed]));
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setSaving(false);
    }
  };

  const syncInventory = async () => {
    try {
      setRefreshing(true);
      await StorageProviderService.syncInventory({
        integration_type: integration,
      });
      refresh();
      ToastMessage.success('Updated Successfully!');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setRefreshing(false);
    }
  };

  const removeAlias = async ({ item, alias, isVariant }: AliasRemoval) => {
    setRemoval(null);
    try {
      await StorageProviderService.updateItems(
        providerId,
        buildAliasRemoval(item.item_id, alias, true),
      );
      ToastMessage.success(
        isVariant
          ? 'Item inventory removed successfully.'
          : 'Item variant unmatched successfully.',
      );
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    }
  };

  const rowContext: InventoryRowContext = {
    providerId,
    flags,
    columns,
    readOnly,
    imperial,
    displaySetting: provider?.display_setting,
    isApiFtp: integration === IntegrationType.apiFtp,
    isLogisticsPlus: integration === IntegrationType.logistics_plus,
    canRemoveAlias: !(isAWD || isFBA),
    pureFulfillable: flags.isFulfillable && !isWarehouse && !flags.isManual,
    permission,
    cartons,
    loose,
    processing,
    onceSubmitted,
    expanded,
    onToggle: itemId =>
      setExpanded(current => {
        const next = new Set(current);
        if (!next.delete(itemId)) next.add(itemId);
        return next;
      }),
    onCartonsChange: (itemId, value, variantId) =>
      setCartons(current => {
        const entry = current[itemId];
        const amount = Math.round(Number(value) || 0);
        return {
          ...current,
          [itemId]: variantId
            ? {
                ...entry,
                byVariant: { ...entry.byVariant, [variantId]: amount },
              }
            : { ...entry, available: amount },
        };
      }),
    onLooseChange: (itemId, value) =>
      setLoose(current => ({
        ...current,
        [itemId]: {
          ...current[itemId],
          newValue: Math.round(Number(value) || 0),
        },
      })),
    onEdit: (item, index) => setAliasTarget({ item, index }),
    onAddAlias: item => setAliasTarget({ item }),
    onSettings: setSettingsItem,
    onRemoveAlias: setRemoval,
  };

  const headers: Record<InventoryColumn, ReactNode> = {
    product: 'Product',
    unfulfillable: 'Unfulfillable',
    outbound: (
      <HintLabel hint={<ReservedHint flags={flags} />}>Reserved</HintLabel>
    ),
    inbound: <HintLabel hint={tooltipTexts.inbound}>Inbound</HintLabel>,
    cartons: (
      <HintLabel
        hint={
          cartonsHeader.inUnits
            ? tooltipTexts.unitsAvailableToShip
            : tooltipTexts.availableCartons
        }
      >
        {cartonsHeader.label}
      </HintLabel>
    ),
    loose: (
      <HintLabel hint={tooltipTexts.unitsAvailableToShip}>Loose Qty</HintLabel>
    ),
    reserved: (
      <HintLabel hint={<ReservedHint flags={flags} />}>Reserved</HintLabel>
    ),
    carton: 'Carton Configuration',
    actions: null,
  };

  return (
    <div className="sk:flex sk:flex-col sk:pb-16">
      <InventoryToolbar
        showEmpty={!!showZeroInventory}
        onShowEmptyChange={setShowZeroInventory}
        imperial={imperial}
        onImperialChange={setImperial}
        hasItems={!loading && items.length > 0}
        total={loading ? undefined : totalData}
        search={search}
        onSearchChange={setSearch}
        canRefresh={REFRESHABLE.includes(integration ?? '')}
        isRefreshing={refreshing}
        onRefresh={syncInventory}
        bulkUpdate={
          isStarterPlan || isDeliverr
            ? undefined
            : {
                href: sheetId
                  ? CommonUtility.getGoogleSheetLinkById(sheetId)
                  : '',
                ...permission,
              }
        }
      />
      {isKindMissing ? (
        <ProviderKindCard
          providerId={providerId}
          isUser={isUser}
          onSaved={() => {
            setKindSaved(true);
            refreshDetails();
          }}
        />
      ) : (
        <TableFrame
          aria-label="Inventory"
          columnCount={columns.length}
          isLoading={loading || detailLoading}
          loadingRows={10}
          sortDescriptor={toSortDescriptor(filter.sort as ISortObject)}
          onSortChange={descriptor =>
            filterChanged(
              'sort',
              fromSortDescriptor(
                descriptor,
                filter.sort as ISortObject,
                FIRST_DIRECTION,
              ),
            )
          }
          pagination={{
            page: filter.page,
            pageSize: filter.limit,
            total: totalData,
            onPageChange: pageChanged,
            onPageSizeChange: size => filterChanged('limit', size),
          }}
          header={
            <TableHeader>
              {columns.map(column => (
                <Column
                  key={column}
                  id={COLUMN_ID[column] ?? column}
                  isRowHeader={column === 'product'}
                  allowsSorting={SORTABLE.has(column)}
                  aria-label={column === 'actions' ? 'Actions' : undefined}
                >
                  {headers[column]}
                </Column>
              ))}
            </TableHeader>
          }
        >
          {items.map(item => (
            <ItemRows key={item.item_id} item={item} context={rowContext} />
          ))}
        </TableFrame>
      )}

      <CartonAliasModal
        target={aliasTarget}
        providerId={providerId}
        imperial={imperial}
        isManual={flags.isManual}
        onClose={() => setAliasTarget(null)}
        onSaved={refresh}
      />
      <ItemSettingsModal
        id={providerId}
        open={!!settingsItem}
        name={settingsItem?.item_title}
        itemId={settingsItem?._id ?? ''}
        blankBoxFulfillment={settingsItem?.is_blank_box}
        closeModal={() => setSettingsItem(null)}
        refresh={refresh}
      />
      <ConfirmModal
        isOpen={!!removal}
        title="Delete carton alias"
        onClose={() => setRemoval(null)}
        onConfirm={() => removal && removeAlias(removal)}
      >
        By confirming, we also reset the carton configurations that were set-up
        for alias <b className="sk:text-fg">{removal?.alias}</b>. Please
        re-match the alias on the Missing Information tab.
      </ConfirmModal>

      {!readOnly && changed.size > 0 ? (
        <SaveBar
          message={`Changes made to ${changed.size} item(s).`}
          onDiscard={reset}
          onSave={save}
          isSaving={saving}
        />
      ) : null}
    </div>
  );
}
